import _toConsumableArray from "@babel/runtime/helpers/esm/toConsumableArray";
import _classCallCheck from "@babel/runtime/helpers/esm/classCallCheck";
import _createClass from "@babel/runtime/helpers/esm/createClass";
import _possibleConstructorReturn from "@babel/runtime/helpers/esm/possibleConstructorReturn";
import _getPrototypeOf from "@babel/runtime/helpers/esm/getPrototypeOf";
import _assertThisInitialized from "@babel/runtime/helpers/esm/assertThisInitialized";
import _inherits from "@babel/runtime/helpers/esm/inherits";
import { createElement } from "@wordpress/element";

/** @format */

/**
 * External dependencies
 */
import { __ } from '@wordpress/i18n';
import classnames from 'classnames';
import { Component, Fragment } from '@wordpress/element';
import { find, first, isEqual, noop, without } from 'lodash';
import PropTypes from 'prop-types';
/**
 * Internal dependencies
 */

import Card from '../card';
import EllipsisMenu from '../ellipsis-menu';
import MenuItem from '../ellipsis-menu/menu-item';
import MenuTitle from '../ellipsis-menu/menu-title';
import Pagination from '../pagination';
import Table from './table';
import TablePlaceholder from './placeholder';
import TableSummary from './summary';
/**
 * This is an accessible, sortable, and scrollable table for displaying tabular data (like revenue and other analytics data).
 * It accepts `headers` for column headers, and `rows` for the table content.
 * `rowHeader` can be used to define the index of the row header (or false if no header).
 *
 * `TableCard` serves as Card wrapper & contains a card header, `<Table />`, `<TableSummary />`, and `<Pagination />`.
 * This includes filtering and comparison functionality for report pages.
 */

var TableCard =
/*#__PURE__*/
function (_Component) {
  _inherits(TableCard, _Component);

  function TableCard(props) {
    var _this;

    _classCallCheck(this, TableCard);

    _this = _possibleConstructorReturn(this, _getPrototypeOf(TableCard).call(this, props));

    var showCols = _this.getShowCols(props.headers);

    _this.state = {
      showCols: showCols
    };
    _this.onColumnToggle = _this.onColumnToggle.bind(_assertThisInitialized(_this));
    _this.onPageChange = _this.onPageChange.bind(_assertThisInitialized(_this));
    return _this;
  }

  _createClass(TableCard, [{
    key: "componentDidUpdate",
    value: function componentDidUpdate(_ref) {
      var prevHeaders = _ref.headers,
          prevQuery = _ref.query;
      var _this$props = this.props,
          headers = _this$props.headers,
          onColumnsChange = _this$props.onColumnsChange,
          query = _this$props.query;
      var showCols = this.state.showCols;

      if (!isEqual(headers, prevHeaders)) {
        /* eslint-disable react/no-did-update-set-state */
        this.setState({
          showCols: this.getShowCols(headers)
        });
        /* eslint-enable react/no-did-update-set-state */
      }

      if (query.orderby !== prevQuery.orderby && !showCols.includes(query.orderby)) {
        var newShowCols = showCols.concat(query.orderby);
        /* eslint-disable react/no-did-update-set-state */

        this.setState({
          showCols: newShowCols
        });
        /* eslint-enable react/no-did-update-set-state */

        onColumnsChange(newShowCols);
      }
    }
  }, {
    key: "getShowCols",
    value: function getShowCols(headers) {
      return headers.map(function (_ref2) {
        var key = _ref2.key,
            visible = _ref2.visible;

        if ('undefined' === typeof visible || visible) {
          return key;
        }

        return false;
      }).filter(Boolean);
    }
  }, {
    key: "getVisibleHeaders",
    value: function getVisibleHeaders() {
      var headers = this.props.headers;
      var showCols = this.state.showCols;
      return headers.filter(function (_ref3) {
        var key = _ref3.key;
        return showCols.includes(key);
      });
    }
  }, {
    key: "getVisibleRows",
    value: function getVisibleRows() {
      var _this$props2 = this.props,
          headers = _this$props2.headers,
          rows = _this$props2.rows;
      var showCols = this.state.showCols;
      return rows.map(function (row) {
        return headers.map(function (_ref4, i) {
          var key = _ref4.key;
          return showCols.includes(key) && row[i];
        }).filter(Boolean);
      });
    }
  }, {
    key: "onColumnToggle",
    value: function onColumnToggle(key) {
      var _this2 = this;

      var _this$props3 = this.props,
          headers = _this$props3.headers,
          query = _this$props3.query,
          onQueryChange = _this$props3.onQueryChange,
          onColumnsChange = _this$props3.onColumnsChange;
      return function () {
        _this2.setState(function (prevState) {
          var hasKey = prevState.showCols.includes(key);

          if (hasKey) {
            // Handle hiding a sorted column
            if (query.orderby === key) {
              var defaultSort = find(headers, {
                defaultSort: true
              }) || first(headers) || {};
              onQueryChange('sort')(defaultSort.key, 'desc');
            }

            var _showCols = without(prevState.showCols, key);

            onColumnsChange(_showCols, key);
            return {
              showCols: _showCols
            };
          }

          var showCols = [].concat(_toConsumableArray(prevState.showCols), [key]);
          onColumnsChange(showCols, key);
          return {
            showCols: showCols
          };
        });
      };
    }
  }, {
    key: "onPageChange",
    value: function onPageChange() {
      var _this$props4 = this.props,
          onPageChange = _this$props4.onPageChange,
          onQueryChange = _this$props4.onQueryChange;

      if (onPageChange) {
        onPageChange.apply(void 0, arguments);
      }

      if (onQueryChange) {
        onQueryChange('paged').apply(void 0, arguments);
      }
    }
  }, {
    key: "render",
    value: function render() {
      var _this3 = this;

      var _this$props5 = this.props,
          actions = _this$props5.actions,
          className = _this$props5.className,
          isLoading = _this$props5.isLoading,
          onQueryChange = _this$props5.onQueryChange,
          onSort = _this$props5.onSort,
          query = _this$props5.query,
          rowHeader = _this$props5.rowHeader,
          rowsPerPage = _this$props5.rowsPerPage,
          showMenu = _this$props5.showMenu,
          summary = _this$props5.summary,
          title = _this$props5.title,
          totalRows = _this$props5.totalRows;
      var showCols = this.state.showCols;
      var allHeaders = this.props.headers;
      var headers = this.getVisibleHeaders();
      var rows = this.getVisibleRows();
      var classes = classnames('woocommerce-table', className);
      return createElement(Card, {
        className: classes,
        title: title,
        action: actions,
        menu: showMenu && createElement(EllipsisMenu, {
          label: __('Choose which values to display', 'woocommerce-admin'),
          renderContent: function renderContent() {
            return createElement(Fragment, null, createElement(MenuTitle, null, __('Columns:', 'woocommerce-admin')), allHeaders.map(function (_ref5) {
              var key = _ref5.key,
                  label = _ref5.label,
                  required = _ref5.required;

              if (required) {
                return null;
              }

              return createElement(MenuItem, {
                checked: showCols.includes(key),
                isCheckbox: true,
                isClickable: true,
                key: key,
                onInvoke: _this3.onColumnToggle(key)
              }, label);
            }));
          }
        })
      }, isLoading ? createElement(Fragment, null, createElement("span", {
        className: "screen-reader-text"
      }, __('Your requested data is loading', 'woocommerce-admin')), createElement(TablePlaceholder, {
        numberOfRows: rowsPerPage,
        headers: headers,
        rowHeader: rowHeader,
        caption: title,
        query: query
      })) : createElement(Table, {
        rows: rows,
        headers: headers,
        rowHeader: rowHeader,
        caption: title,
        query: query,
        onSort: onSort || onQueryChange('sort')
      }), createElement(Pagination, {
        key: parseInt(query.paged) || 1,
        page: parseInt(query.paged) || 1,
        perPage: rowsPerPage,
        total: totalRows,
        onPageChange: this.onPageChange,
        onPerPageChange: onQueryChange('per_page')
      }), summary && createElement(TableSummary, {
        data: summary
      }));
    }
  }]);

  return TableCard;
}(Component);

TableCard.propTypes = {
  /**
   * An array of column headers (see `Table` props).
   */
  headers: PropTypes.arrayOf(PropTypes.shape({
    hiddenByDefault: PropTypes.bool,
    defaultSort: PropTypes.bool,
    isSortable: PropTypes.bool,
    key: PropTypes.string,
    label: PropTypes.oneOfType([PropTypes.string, PropTypes.node]),
    required: PropTypes.bool
  })),

  /**
   * A list of IDs, matching to the row list so that ids[ 0 ] contains the object ID for the object displayed in row[ 0 ].
   */
  ids: PropTypes.arrayOf(PropTypes.number),

  /**
   * Defines if the table contents are loading.
   * It will display `TablePlaceholder` component instead of `Table` if that's the case.
   */
  isLoading: PropTypes.bool,

  /**
   * A function which returns a callback function to update the query string for a given `param`.
   */
  onQueryChange: PropTypes.func,

  /**
   * A function which returns a callback function which is called upon the user changing the visiblity of columns.
   */
  onColumnsChange: PropTypes.func,

  /**
   * A function which is called upon the user changing the sorting of the table.
   */
  onSort: PropTypes.func,

  /**
   *  An object of the query parameters passed to the page, ex `{ page: 2, per_page: 5 }`.
   */
  query: PropTypes.object,

  /**
   * An array of arrays of display/value object pairs (see `Table` props).
   */
  rowHeader: PropTypes.oneOfType([PropTypes.number, PropTypes.bool]),

  /**
   * Which column should be the row header, defaults to the first item (`0`) (see `Table` props).
   */
  rows: PropTypes.arrayOf(PropTypes.arrayOf(PropTypes.shape({
    display: PropTypes.node,
    value: PropTypes.oneOfType([PropTypes.string, PropTypes.number, PropTypes.bool])
  }))).isRequired,

  /**
   * The total number of rows to display per page.
   */
  rowsPerPage: PropTypes.number.isRequired,

  /**
   * Boolean to determine whether or not ellipsis menu is shown.
   */
  showMenu: PropTypes.bool,

  /**
   * An array of objects with `label` & `value` properties, which display in a line under the table.
   * Optional, can be left off to show no summary.
   */
  summary: PropTypes.arrayOf(PropTypes.shape({
    label: PropTypes.node,
    value: PropTypes.oneOfType([PropTypes.string, PropTypes.number])
  })),

  /**
   * The title used in the card header, also used as the caption for the content in this table.
   */
  title: PropTypes.string.isRequired,

  /**
   * The total number of rows (across all pages).
   */
  totalRows: PropTypes.number.isRequired
};
TableCard.defaultProps = {
  isLoading: false,
  onQueryChange: noop,
  onColumnsChange: noop,
  onSort: undefined,
  query: {},
  rowHeader: 0,
  rows: [],
  showMenu: true
};
export default TableCard;
//# sourceMappingURL=index.js.map